// TableSetup — assign characters (human or AI) to seats, then enter play.
import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import type { BuildCharacterRequest, ControllerKind, FullGame, Seat } from "../lib/types";
import { useMeta } from "../hooks/useMeta";
import { titleizeSlug } from "../lib/format";
import { CenterLoad, Modal } from "./ui";
import { CharacterBuilder } from "./CharacterBuilder";
import { useToast } from "../hooks/useToast";

export function TableSetup({
  gameId,
  onPlay,
  onBack,
}: {
  gameId: string;
  onPlay: () => void;
  onBack: () => void;
}) {
  const { meta } = useMeta();
  const toast = useToast();
  const [game, setGame] = useState<FullGame | null>(null);
  const [buildSeat, setBuildSeat] = useState<{ seat: Seat; kind: ControllerKind } | null>(null);
  const [copied, setCopied] = useState(false);
  // Track filled-seat count across polls so we can toast when someone new sits down.
  const filledRef = useRef<number | null>(null);

  async function load() {
    try { setGame(await api.game(gameId)); }
    catch (e) { toast.push(e instanceof Error ? e.message : "Failed to load", "error"); }
  }
  useEffect(() => { load(); }, [gameId]); // eslint-disable-line

  // Poll while sitting on the setup screen so the host sees seats fill in real time
  // as invited players join, without needing a manual refresh.
  useEffect(() => {
    const t = window.setInterval(async () => {
      if (buildSeat) return; // don't yank state out from under an open builder
      try {
        const g = await api.game(gameId);
        const filled = g.seats.filter((s) => s.character_id).length;
        if (filledRef.current !== null && filled > filledRef.current) {
          toast.push(filled === g.seats.length ? "The table is full — everyone's seated!" : "A new player took a seat", "success");
        }
        filledRef.current = filled;
        setGame(g);
      } catch { /* transient — try again next tick */ }
    }, 5000);
    return () => window.clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId, buildSeat]);

  // Seed the filled-count baseline once the first load lands.
  useEffect(() => {
    if (game && filledRef.current === null) {
      filledRef.current = game.seats.filter((s) => s.character_id).length;
    }
  }, [game]);

  const inviteUrl = `${window.location.origin}${window.location.pathname}#/setup/${gameId}`;
  async function copyInvite() {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      toast.push("Invite link copied", "success");
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked (insecure context / permissions) — surface the link to copy by hand.
      toast.push("Copy failed — select the link and copy it manually", "error");
    }
  }

  async function clearSeat(seatId: string) {
    if (!confirm("Remove this character from the table?")) return;
    try { await api.clearSeat(gameId, seatId); await load(); toast.push("Seat cleared", "info"); }
    catch { toast.push("Failed to clear seat", "error"); }
  }

  async function addSeat() {
    try { await api.addSeat(gameId); await load(); toast.push("New seat added", "success"); }
    catch (e) { toast.push(e instanceof Error ? e.message : "Couldn't add a seat", "error"); }
  }

  async function commit(seatId: string, kind: ControllerKind, req: BuildCharacterRequest, persona: string, label: string) {
    await api.assignSeat(gameId, {
      seat_id: seatId, controller_kind: kind, label, persona: persona || undefined, character: req,
    });
    setBuildSeat(null);
    await load();
    toast.push(`${label} takes a seat`, "success");
  }

  if (!game || !meta) return <div className="screen"><CenterLoad /></div>;

  const total = game.seats.length;
  const filled = game.seats.filter((s) => s.character_id).length;

  return (
    <div className="screen">
      <div className="container">
        <div className="card setup-banner">
          <div style={{ fontSize: 40 }}>🪑</div>
          <div className="setup-banner__body">
            <h2>{game.name}</h2>
            <div className="muted" style={{ fontSize: 13, textTransform: "capitalize" }}>{game.dm_style} tone</div>
            {game.setting && <div className="setting">{game.setting}</div>}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <button className="btn btn--primary btn--lg" disabled={filled === 0} onClick={onPlay}>
              Enter the Table →
            </button>
            <span className="hint" style={{ textAlign: "right" }}>
              {filled === 0 ? "Seat at least one character" : `${filled} of ${total} ${total === 1 ? "seat" : "seats"} filled`}
            </span>
          </div>
        </div>

        <div className="card invite-bar" style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginTop: 12 }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div className="label" style={{ marginBottom: 4 }}>Invite players</div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
              Share this link — anyone who opens it lands right here to claim a seat.
            </div>
            <input className="field" readOnly value={inviteUrl}
              onFocus={(e) => e.currentTarget.select()}
              style={{ fontSize: 12, fontFamily: "var(--mono)" }} />
          </div>
          <button className="btn btn--primary" onClick={copyInvite}>
            {copied ? "✓ Copied" : "🔗 Copy invite link"}
          </button>
        </div>

        <div className="seats-grid">
          {game.seats.map((seat) => (
            <SeatCard key={seat.id} seat={seat}
              onClear={() => clearSeat(seat.id)}
              onBuild={(kind) => setBuildSeat({ seat, kind })} />
          ))}
          {game.seats.length < 8 && (
            <button className="card seat-card seat-card--add" onClick={addSeat}>
              <div style={{ fontSize: 30, opacity: 0.6 }}>＋</div>
              <div className="muted">Add a seat</div>
              <div className="hint">for a new player to join</div>
            </button>
          )}
        </div>

        <div className="mt24">
          <button className="btn btn--ghost" onClick={onBack}>← Back to campaigns</button>
        </div>
      </div>

      {buildSeat && (
        <Modal wide onClose={() => setBuildSeat(null)}>
          <CharacterBuilder
            meta={meta}
            controllerKind={buildSeat.kind}
            onCancel={() => setBuildSeat(null)}
            onCommit={(req, persona, label) => commit(buildSeat.seat.id, buildSeat.kind, req, persona, label)}
          />
        </Modal>
      )}
    </div>
  );
}

function SeatCard({
  seat,
  onClear,
  onBuild,
}: {
  seat: Seat;
  onClear: () => void;
  onBuild: (kind: ControllerKind) => void;
}) {
  const ch = seat.character;
  if (!ch) {
    return (
      <div className="card seat-card seat-card--empty">
        <div style={{ fontSize: 30, opacity: 0.5 }}>＋</div>
        <div className="muted">{seat.label} — empty</div>
        <div className="flex">
          <button className="btn btn--sm" onClick={() => onBuild("human")}>👤 Human</button>
          <button className="btn btn--sm" onClick={() => onBuild("ai")}>🧠 AI</button>
        </div>
      </div>
    );
  }
  return (
    <div className="card seat-card">
      <div className="seat-card__head">
        <div className="seat-name">{ch.name}</div>
        <span className={`chip ${seat.kind === "ai" ? "chip--ai" : ""}`}>
          {seat.kind === "ai" ? "🧠 AI" : "👤 Human"}
        </span>
      </div>
      <div className="muted" style={{ fontSize: 13 }}>
        {titleizeSlug(ch.subrace || ch.race)}{" "}
        {ch.classes.map((c) => `${titleizeSlug(c.class)} ${c.level}`).join(" / ")}
      </div>
      <div className="seat-card__stats">
        <div className="seat-stat"><div className="v">{ch.ac}</div><div className="k">AC</div></div>
        <div className="seat-stat"><div className="v">{ch.max_hp}</div><div className="k">HP</div></div>
        <div className="seat-stat"><div className="v">{ch.level}</div><div className="k">LVL</div></div>
        {ch.spell_save_dc != null && (
          <div className="seat-stat"><div className="v">{ch.spell_save_dc}</div><div className="k">DC</div></div>
        )}
      </div>
      {seat.persona && <div className="seat-card__persona">{seat.persona}</div>}
      <div className="seat-card__foot">
        <button className="btn btn--ghost btn--sm btn--danger grow" onClick={onClear}>Remove</button>
      </div>
    </div>
  );
}
